import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Booking } from '../../types/booking';
import { bookingEndpoints } from '../../services/api/endpoints/booking.endpoint';
import { logger } from '../../utils/logger';

interface BookingState {
  items: Booking[];
  externalEvents: any[];
  selectedBooking: Booking | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: BookingState = {
  items: [],
  externalEvents: [],
  selectedBooking: null,
  isLoading: false,
  error: null,
  lastFetched: null,
};

// Fetch bookings thunk
export const fetchBookings = createAsyncThunk(
  'bookings/fetchAll',
  async ({
    organizationId,
    startDate,
    endDate,
  }: {
    organizationId: string;
    startDate?: string;
    endDate?: string;
  }) => {
    try {
      const bookings = await bookingEndpoints.listBookings(organizationId, startDate, endDate);
      return bookings;
    } catch (error) {
      logger.error('Failed to fetch bookings:', error);
      throw error;
    }
  }
);

// Fetch bookings by Resource Id with date Range
export const fetchBookingsByResourceId = createAsyncThunk(
  'bookings/fetchByResourceId',
  async ({
    organizationId,
    resourceId,
    startDate,
    endDate,
  }: {
    organizationId: string;
    resourceId: number;
    startDate?: string;
    endDate?: string;
  }) => {
    try {
      const { bookings, externalEvents } = await bookingEndpoints.listBookingsByResourceId(
        organizationId,
        resourceId,
        startDate,
        endDate
      );
      return { bookings, externalEvents };
    } catch (error) {
      logger.error('Failed to fetch bookings by resource ID:', error);
      throw error;
    }
  }
);

// Get booking by ID thunk
export const getBookingById = createAsyncThunk(
  'bookings/getById',
  async (
    { organizationId, bookingId }: { organizationId: string; bookingId: number },
    { getState }
  ) => {
    const state = getState() as { bookings: BookingState };
    const existingBooking = state.bookings.items.find(b => b.id === bookingId);

    // If booking exists and was fetched recently (within last 5 minutes), return it
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    if (
      existingBooking &&
      state.bookings.lastFetched &&
      Date.now() - state.bookings.lastFetched < CACHE_DURATION
    ) {
      return existingBooking;
    }

    // Fetch the specific booking by ID from API
    const booking = await bookingEndpoints.getBookingById(organizationId, bookingId);
    return booking;
  }
);

const bookingSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {
    clearSelectedBooking: state => {
      state.selectedBooking = null;
    },
    clearBookings: state => {
      state.items = [];
      state.selectedBooking = null;
      state.lastFetched = null;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch bookings
      .addCase(fetchBookings.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.items = action.payload;
        state.isLoading = false;
        state.error = null;
        state.lastFetched = Date.now();
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch bookings';
      })
      .addCase(fetchBookingsByResourceId.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBookingsByResourceId.fulfilled, (state, action) => {
        state.items = action.payload.bookings;
        state.externalEvents = action.payload.externalEvents;
        state.isLoading = false;
        state.error = null;
        state.lastFetched = Date.now();
      })
      .addCase(fetchBookingsByResourceId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch bookings by resource ID';
      })
      // Get booking by ID
      .addCase(getBookingById.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBookingById.fulfilled, (state, action) => {
        state.selectedBooking = action.payload;
        state.isLoading = false;
        state.error = null;
        // Update the booking in items array if it exists
        const index = state.items.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        } else {
          state.items.push(action.payload);
        }
      })
      .addCase(getBookingById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch booking';
      });
  },
});

export const { clearSelectedBooking, clearBookings } = bookingSlice.actions;
export default bookingSlice.reducer;
